import { reportError } from "utils/diagnostics";
import { Box, Typography } from "@mui/material";
import { PrimaryButton } from "components/common/Buttons";
import { Popup } from "components/common/popup/popup.component";
import { Spinner } from "components/common/spinner/spinner.component";
import { useRequest } from "hooks/useRequest";
import { VPN_CONNECTED } from "pages/Devices/Detail/device-detail.page";
import { disconnectDeviceVpn, updateVpnDeviceStatus } from "api/DeviceHelpers";
import axiosInst from "api/config/axiosConfig";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 100; // ~5 minutes
const VPN_CONNECTION_FAILED = "Connection Failed";
const VPN_ERROR = "Error";

export default function LaunchMissionControlPage() {
  const navigate = useNavigate();
  const { sn: serialNumber } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");

  // Initial VPN status (loaded once on mount). Polling during reconnect uses
  // direct axios calls so the iframe is not affected.
  const { data: vpnData } = useRequest(`vpnUIHook?serialNumber=${serialNumber}`);

  useEffect(() => {
    if (vpnData?.mcUrl && !iframeUrl) {
      setIframeUrl(vpnData.mcUrl);
    }
  }, [vpnData, iframeUrl]);

  const onIframeLoad = () => {
    setIsLoading(false);
  };

  const handleReconnect = async () => {
    try {
      setIsError(false);
      setIsReconnecting(true);
      setIsLoading(true);

      await disconnectDeviceVpn(serialNumber);
      await updateVpnDeviceStatus(serialNumber);

      let result = null;
      for (let i = 0; i < MAX_POLL_ATTEMPTS; i += 1) {
        // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        // eslint-disable-next-line no-await-in-loop
        const res = await axiosInst.get(`/beta/vpnUIHook?serialNumber=${serialNumber}`);
        result = res.data;
        // Only stop on a terminal status. Anything else (In Progress,
        // VPN initialized, Not Connected, etc.) means keep waiting.
        if (
          result?.vpnStatus === VPN_CONNECTED ||
          result?.vpnStatus === VPN_CONNECTION_FAILED ||
          result?.vpnStatus === VPN_ERROR
        ) {
          break;
        }
      }

      if (result?.vpnStatus === VPN_CONNECTED && result?.mcUrl) {
        setIframeUrl(result.mcUrl);
      } else {
        setIsError(true);
      }
    } catch (error) {
      reportError("pages/launch-mission-control/launch-mission-control.page.handleReconnect", error);
      setIsError(true);
    } finally {
      setIsReconnecting(false);
      setIsLoading(false);
    }
  };

  return (
    <>
      <iframe title="Mission Control" width="100%" height="100%" src={iframeUrl} onLoad={onIframeLoad} />
      <Box sx={{ position: "fixed", bottom: 32, right: 24, zIndex: 1300 }}>
        <PrimaryButton onClick={handleReconnect} disabled={isReconnecting}>
          {isReconnecting ? "Re-Connecting..." : "Re-Connect"}
        </PrimaryButton>
      </Box>
      <Popup open={isLoading} title={isReconnecting ? "Re-connecting VPN..." : "Page is loading..."}>
        <Spinner />
      </Popup>
      <Popup
        open={isError}
        title="Info"
        primaryBtn={{
          onClick: handleReconnect,
          text: "Re-Connect",
          disabled: isReconnecting,
        }}
        secondaryBtn={{
          onClick: () => navigate(`/devices/${serialNumber}`),
          text: "Cancel",
        }}
      >
        <Typography>Mission Control is not reachable. The device may be unreachable through the VPN tunnel.</Typography>
      </Popup>
    </>
  );
}
